import { Grid } from "@mui/material";
import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { MyBEE123HomepageService } from "../../../Component/Api/MyBEE123HomePage/MyBEE123HomepageService";
import { SupportRequestService } from "../../../Component/Api/SupportRequest/SupportRequestService";
import { DataTable } from "../../../Component/Common/Components/DataTable/DataTable";
import { IFilter, IFilterValue } from "../../../Component/Common/Components/DataTable/Filters";
import { showConfirmDialog } from "../../../Component/Common/Components/SweetAlert/SweetAlert";
import { ColumnSortOption, SortDirections } from "../../../Component/Model/Dto/ColumnSortOption";
import { Support } from "../../../Component/Model/Dto/Support/SupportRequest";
import { EntriesPerPage } from "../../../Component/Model/Table/Table";
import { defaultObject } from "../../../Helpers/typescriptHelper";
import { SupportModal } from "./SupportTicketModal";
import { SupportTicketTableMapper } from "./SupportTicketTableColumn";

export const SupportTickets = () => {
    const [openModal, setOpenModal] = React.useState<boolean>(false);
    const [ticketData, setTicketData] = React.useState<Support.DtoOut[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [selectedTicketId, setSelectedTicketId] = React.useState<string | undefined>(undefined);
    const [sortColumnNames, setSortColumnNames] = React.useState<string[]>([]);
    const [sortDirections, setSortDirections] = React.useState<SortDirections[]>([]);
    const [statuses, setStatuses] = React.useState<number[]>([]);
    const [userScorecards, setUserScorecards] = React.useState<IFilterValue[]>([]);
    const [scorecardId, setScorecardId] = React.useState('');

    const [searchValue, setSearchValue] = React.useState<string>('');

    const supportTicketFilters: Array<IFilter> = [
        {
            label: 'Status:',
            values: [
                { key: 'Open', val: 1 },
                { key: 'In Progress', val: 2 },
                { key: 'Resolved', val: 3 },
            ],
            type: 4, propname: 'Statuses'
        },
        {
            label: 'Scorecard:',
            values: userScorecards,
            type: 4, propname: 'ScorecardId'
        }
    ];

    const filtersChanged = (filterProps: any) => {
        if (filterProps.Statuses.length !== 0) {
            setStatuses([filterProps.Statuses]);
        } else {
            setStatuses([]);
        }

        if (filterProps.ScorecardId.length !== 0) {
            setScorecardId(filterProps.ScorecardId);
        } else {
            setScorecardId('');
        }
        setPageNumber(1);
    }

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setSortColumnNames([sortOption.ColumnName]);
        setSortDirections([sortOption.SortDirection]);
        setPageNumber(1);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
    }

    const handleCloseForm = () => {
        setOpenModal(false);
        setSelectedTicketId(undefined);
        fetchTableData();
    }

    const handleEdit = (id: string) => {
        setSelectedTicketId(id);
        setOpenModal(true);
    }

    const handleResolve = (id: string) => {
        showConfirmDialog('Resolve Ticket', 'Are you sure you want to resolve ticket?', 'Yes', async () => {
            try {
                await SupportRequestService.ResolveTicket(id);
                toast.success("Ticket resolved")
                fetchTableData();
            } catch (error) {
                console.log(error);
            }
        });
    }

    const ticketSearchRequest: Support.SearchRequest = {
        PageNumber: pageNumber as number,
        PageSize: entriesPerPage.defaultValue as number,
        SearchValue: searchValue,
        SortColumnNames: sortColumnNames,
        SortDirections: sortDirections,
        Statuses: statuses,
        ScorecardId: scorecardId
    };

    const getUserScorecards = async () => {
        try {
            const scorecardSearchResult = await MyBEE123HomepageService.getAllUserScorecards();
            const scorecardData: IFilterValue[] = scorecardSearchResult.data.map((item) => { return { key: item.Name, val: item.ID } });
            setUserScorecards(scorecardData);
        } catch (error) {
            console.log(error)
        }
    };

    const fetchTableData = async () => {
        setIsLoading(true);
        try {
            let searchResult = await SupportRequestService.Search(ticketSearchRequest);
            if (searchResult.data) {
                setTicketData(searchResult.data.dataObject);
                setFilteredItemsCount(searchResult.data.filteredItemsCount);
                setTotalItemsCount(searchResult.data.totalItemsCount);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getUserScorecards();
    }, []);

    useEffect(() => {
        fetchTableData();
    }, [searchValue, entriesPerPage, pageNumber, sortColumnNames, sortDirections, statuses, scorecardId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddSupportTicket = () => {
        setSelectedTicketId(undefined);
        setOpenModal(true);
    }

    const bulkActions = [
        {
            label: "Add New Support Ticket",
            action: handleAddSupportTicket
        }
    ]

    const supportTicketTableData = SupportTicketTableMapper(ticketData, handleEdit, handleResolve);
    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <DataTable
                        key={"supportTicketTable"}
                        table={supportTicketTableData}
                        entriesPerPage={entriesPerPage}
                        pageNumber={pageNumber}
                        filteredItemsCount={filteredItemsCount}
                        totalItemsCount={totalItemsCount}
                        entriesPerPageChanged={entriesPerPageChanged}
                        pageNumberChanged={pageNumberChanged}
                        isLoading={isLoading}
                        bulkActions={bulkActions}
                        manualSorting={true}
                        columnSortingChanged={columnSortingChanged}
                        canSearch={true}
                        searchValueChanged={searchValueChanged}
                        filters={supportTicketFilters}
                        filtersChanged={filtersChanged}

                    ></DataTable>
                </Grid>

                {openModal && <SupportModal
                    openModal={openModal}
                    handleClose={handleCloseForm}
                    supportTicketId={selectedTicketId}
                    setSupportTicketId={setSelectedTicketId}
                />}

            </Grid>
        </>
    )
}